import type {
  AggregateBreakdownRequest,
  AggregateBreakdownResponse,
  AggregateDrilldownRequest,
  AggregateDrilldownResponse,
  AggregateGridRequest,
  AggregateGridResponse,
  AggregateSummaryRequest,
  AggregateSummaryResponse,
  PlaceDetailsRequest,
  PlaceDetailsResponse,
  RuntimeConfig,
} from "../../shared/types";

async function apiRequest<T>(
  path: string,
  options?: {
    body?: unknown;
    method?: "GET" | "POST";
    signal?: AbortSignal;
  },
): Promise<T> {
  const response = await fetch(path, {
    method: options?.method ?? (options?.body ? "POST" : "GET"),
    headers: options?.body
      ? {
          "content-type": "application/json",
        }
      : undefined,
    body: options?.body ? JSON.stringify(options.body) : undefined,
    signal: options?.signal,
  });

  if (!response.ok) {
    let message = `Request failed with ${response.status}`;

    try {
      const payload = (await response.json()) as { error?: string };
      if (payload.error) {
        message = payload.error;
      }
    } catch {
      // Ignore JSON parsing errors and keep the generic status message.
    }

    throw new Error(message);
  }

  return (await response.json()) as T;
}

export function loadRuntimeConfig(signal?: AbortSignal): Promise<RuntimeConfig> {
  return apiRequest<RuntimeConfig>("/api/config", {
    signal,
  });
}

export function fetchSummary(
  payload: AggregateSummaryRequest,
  signal?: AbortSignal,
): Promise<AggregateSummaryResponse> {
  return apiRequest<AggregateSummaryResponse>("/api/aggregate/summary", {
    body: payload,
    signal,
  });
}

export function fetchGrid(
  payload: AggregateGridRequest,
  signal?: AbortSignal,
): Promise<AggregateGridResponse> {
  return apiRequest<AggregateGridResponse>("/api/aggregate/grid", {
    body: payload,
    signal,
  });
}

export function fetchBreakdown(
  payload: AggregateBreakdownRequest,
  signal?: AbortSignal,
): Promise<AggregateBreakdownResponse> {
  return apiRequest<AggregateBreakdownResponse>("/api/aggregate/breakdown", {
    body: payload,
    signal,
  });
}

export function fetchDrilldown(
  payload: AggregateDrilldownRequest,
  signal?: AbortSignal,
): Promise<AggregateDrilldownResponse> {
  return apiRequest<AggregateDrilldownResponse>("/api/aggregate/drilldown", {
    body: payload,
    signal,
  });
}

export function fetchPlaceDetails(
  payload: PlaceDetailsRequest,
  signal?: AbortSignal,
): Promise<PlaceDetailsResponse> {
  return apiRequest<PlaceDetailsResponse>("/api/places/details", {
    body: payload,
    signal,
  });
}
